import { quotes } from "@fm/api";
import type {
  Quote,
  ApiResponse,
  ListResponse,
  PricingRequest,
  PricingBreakdown,
} from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import { getApiBaseUrl } from "../lib/config";

export interface UseQuotesOptions {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
}

export interface CreateQuotePayload {
  adults: number;
  children: number;
  infants: number;
  pets: number;
  pick_up: Date | string;
  return: Date | string;
  client_name: string;
  client_phone?: string;
  client_email: string;
  notes?: string;
  quotable_type?: "fleet_category" | "fleet_vehicle";
  quotable_id?: string;
  daily_rates?: Array<{
    date: string;
    quoted_amount: string;
    rate_plan_id: string;
    notes: string;
    currency?: string;
  }>;
}

export interface UseQuotesReturn {
  createQuote: (quote: CreateQuotePayload) => Promise<ApiResponse<Quote>>;
  createBooking: (quoteId: string) => Promise<ApiResponse<Quote>>;
  getQuote: (quoteId: string) => Promise<ApiResponse<Quote>>;
  getQuotes: (params?: {
    clientName?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<ListResponse<Quote>>;
  updateQuote: (
    quoteId: string,
    quote: Partial<CreateQuotePayload>,
  ) => Promise<ApiResponse<Quote>>;
  deleteQuote: (quoteId: string) => Promise<void>;
  calculatePricing: (
    pricingRequest: PricingRequest,
  ) => Promise<ApiResponse<PricingBreakdown>>;
}

export function useQuotes(options: UseQuotesOptions): UseQuotesReturn {
  const { apiKey, fleetId, apiBaseUrl } = options;

  // Create API client with the provided apiKey
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  const createQuote = async (quote: CreateQuotePayload) => {
    const quoteData = {
      ...quote,
      pick_up: quote.pick_up,
      return: quote.return,
    };
    return quotes.create(apiClient, fleetId, quoteData);
  };

  const createBooking = async (quoteId: string) => {
    return quotes.createBooking(apiClient, fleetId, quoteId);
  };

  const getQuote = async (quoteId: string) => {
    return quotes.get(apiClient, fleetId, quoteId);
  };

  const getQuotes = async (params?: {
    clientName?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    return quotes.list(apiClient, fleetId, params);
  };

  const updateQuote = async (
    quoteId: string,
    quote: Partial<CreateQuotePayload>,
  ) => {
    return quotes.update(apiClient, fleetId, quoteId, quote as any);
  };

  const deleteQuote = async (quoteId: string) => {
    return quotes.delete(apiClient, fleetId, quoteId);
  };

  const calculatePricing = async (pricingRequest: PricingRequest) => {
    return quotes.calculatePricing(apiClient, fleetId, pricingRequest);
  };

  return {
    createQuote,
    createBooking,
    getQuote,
    getQuotes,
    updateQuote,
    deleteQuote,
    calculatePricing,
  };
}
